/**
 * GrowCard — NiV clinic detail → "ניהול" block for Grow payments (Phase 8.1).
 * Connection state, 30-day counts, last callback/error, recent links, and
 * admin actions: environment flip, partner-flow flag, partner status, commission,
 * attach identifiers (partner hand-off), force disconnect.
 * Every write goes through `nivGrowAdmin` (runNivGrowAction). CK-9988.
 */

import { useState } from "react";
import { ArrowsClockwise, CreditCard, LinkSimple, MagnifyingGlass, Plugs, Warning } from "@phosphor-icons/react";
import ConfirmDialog from "../../../../../components/ui/ConfirmDialog";
import LoaderLoop from "../../../../../components/shared/loader/LoaderLoop";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  useGetNivGrowClinicStateQuery,
  useGetNivGrowLinksQuery,
  useRunNivGrowActionMutation,
} from "../../../../../store/services/nivApi";
import { formatCurrency, formatDateTime } from "../clinicDetailConstants";

const STATUS_HE = { creating: "נוצר…", created: "נוצר", sent: "נשלח", paid: "שולם", expired: "פג תוקף", cancelled: "בוטל", failed: "נכשל" };
const METHOD_HE = { 1: "אשראי", 5: "PayBox", 6: "Bit", 13: "Apple Pay", 14: "Google Pay", 15: "העברה" };
const ID_RE = /^[A-Za-z0-9_-]{4,64}$/;

export default function GrowCard({ clinic, refetch }) {
  const clinicId = clinic?.clinicId;
  const { showSuccess, showError } = useToast();
  const { data: state, isLoading, refetch: refetchState } = useGetNivGrowClinicStateQuery({ clinicId }, { skip: !clinicId });
  const { data: links = [] } = useGetNivGrowLinksQuery({ clinicId, limit: 8 }, { skip: !clinicId || !state?.isGrowIntegrated });
  const [run, { isLoading: running }] = useRunNivGrowActionMutation();

  const [confirm, setConfirm] = useState(null); // { action, payload, title, message, type }
  const [attach, setAttach] = useState({ open: false, userId: "", pageCode: "", accountPageCode: "", environment: "sandbox" });
  const [commission, setCommission] = useState("");

  const act = async (action, payload, okMsg) => {
    const res = await run({ action, payload: { clinicId, ...payload } });
    if (res.error) {
      console.error("[CK-9988] NiV Grow action failed", { errorCode: "CK-9988", action, errorMessage: res.error.message, clinicId, timestamp: new Date().toISOString() });
      showError(res.error.message || "הפעולה נכשלה");
      return false;
    }
    showSuccess(okMsg);
    refetch?.();
    refetchState();
    return true;
  };

  const gi = state?.growIntegration || {};
  const connected = state?.isGrowIntegrated === true;
  const env = state?.secure?.environment || gi.environment || null;
  const c = state?.counts30d || {};

  return (
    <section className="ncd2-card" id="ncd2-block-grow">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon"><CreditCard size={15} weight="fill" /></span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">תשלומים</span>
          <h3 className="ncd2-card-title">
            Grow
            {connected ? <span className="ncd2-badge ncd2-badge-plan">מחובר</span> : <span className="ncd2-badge ncd2-badge-muted">לא מחובר</span>}
            {connected && env === "sandbox" && <span className="ncd2-badge ncd2-badge-reminder">Sandbox</span>}
            {gi.partnerStatus === "pending" && <span className="ncd2-badge ncd2-badge-muted">ממתין לאישור Grow</span>}
          </h3>
        </div>
        <button type="button" className="ncd2-icon-btn" onClick={() => refetchState()} title="רענון" disabled={isLoading}>
          <ArrowsClockwise size={14} className={isLoading ? "spinning" : ""} />
        </button>
      </header>

      {isLoading && !state ? (
        <LoaderLoop size="xs" />
      ) : (
        <>
          <div className="ncd2-read-grid">
            <div className="ncd2-read-row"><span className="ncd2-read-label">מקור חיבור</span><span className="ncd2-read-value">{gi.source === "partner" ? "דרך CliniKit" : gi.source === "manual" ? "ידני" : "—"}</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">מזהה עסק</span><span className="ncd2-read-value" dir="ltr">{state?.secure?.userIdMasked || "—"}</span></div>
            {connected && (
              <div className="ncd2-read-row"><span className="ncd2-read-label">קודי עמוד</span><span className="ncd2-read-value">דרישת תשלום: {state?.secure?.pageCodeSource === "platform" ? "של הפלטפורמה" : "של הקליניקה"} · חשבון (מיתוג): {state?.secure?.hasAccountPageCode ? "קיים" : "חסר"}</span></div>
            )}
            <div className="ncd2-read-row"><span className="ncd2-read-label">אמצעי תשלום</span><span className="ncd2-read-value">{(gi.methods || []).map((m) => METHOD_HE[m] || m).join(" · ") || "—"}</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">30 יום</span><span className="ncd2-read-value">{c.total || 0} קישורים · {c.paid || 0} שולמו ({formatCurrency(c.paidSum || 0)}) · {c.expired || 0} פג תוקף · {c.failed || 0} נכשלו</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">פעילים כעת</span><span className="ncd2-read-value">{state?.activeLinks ?? 0}</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">עדכון אחרון מ-Grow</span><span className="ncd2-read-value">{state?.lastCallbackAt ? formatDateTime(state.lastCallbackAt) : "—"}</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">שליחה אוטומטית</span><span className="ncd2-read-value">{gi.autoSendOnCompletion ? "פעילה" : "כבויה"} · קבלה למטופל {gi.sendReceiptToPatient ? "כן" : "לא"}</span></div>
            <div className="ncd2-read-row"><span className="ncd2-read-label">עמלת פלטפורמה</span><span className="ncd2-read-value">{state?.secure?.companyCommission ? `₪${state.secure.companyCommission}` : "—"}</span></div>
            {gi.lastError && (
              <div className="ncd2-read-row"><span className="ncd2-read-label"><Warning size={13} weight="fill" /> שגיאה אחרונה</span><span className="ncd2-read-value">Grow {gi.lastError.growErrorId ?? "—"} · {gi.lastError.fn || ""} · {gi.lastError.at ? formatDateTime(gi.lastError.at) : ""}</span></div>
            )}
            {!state?.planAllows && <div className="ncd2-read-row"><span className="ncd2-read-label">מסלול</span><span className="ncd2-read-value ncd2-badge ncd2-badge-danger">חסום ב-planOverrides</span></div>}
          </div>

          {connected && links.length > 0 && (
            <div className="ncd2-table-wrap">
              <table className="ncd2-table">
                <thead><tr><th>סטטוס</th><th>מטופל</th><th>סכום</th><th>נוצר</th><th>אישור</th><th></th></tr></thead>
                <tbody>
                  {links.map((l) => (
                    <tr key={l.id}>
                      <td>{STATUS_HE[l.status] || l.status}{l.growErrorId != null ? ` (${l.growErrorId})` : ""}</td>
                      <td>{l.patientName || "—"}</td>
                      <td className="ncd2-table-key">{formatCurrency(l.sum || 0)}</td>
                      <td>{l.createdAt ? formatDateTime(l.createdAt) : "—"}</td>
                      <td>{l.approveStatus || "—"}</td>
                      <td>
                        {(l.status === "sent" || l.status === "created") && l.paymentLinkProcessId && (
                          <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" disabled={running} title="getPaymentLinkInfo — מקרי קיצון בלבד לפי Grow" onClick={() => setConfirm({ action: "reconcileLink", payload: { linkId: l.id }, title: "בדיקה מול Grow", message: "קריאה ל-getPaymentLinkInfo. Grow ביקשו להשתמש בה במקרי קיצון בלבד (למשל חיווי שלא הגיע). להמשיך?", type: "warning", ok: "הבדיקה הושלמה — ראו סטטוס הקישור" })}>
                            <MagnifyingGlass size={13} /> בדיקה מול Grow
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="ncd2-switch-row">
            <span>הרשמה דרך CliniKit (partner flow)</span>
            <button type="button" className={`ncd2-switch ${gi.partnerFlowEnabled ? "is-on" : ""}`} onClick={() => act("setPartnerFlow", { enabled: !gi.partnerFlowEnabled }, "עודכן")} disabled={running} aria-pressed={!!gi.partnerFlowEnabled}>
              <span className="ncd2-switch-knob" />
            </button>
          </div>

          <div className="ncd2-field">
            <span className="ncd2-field-label">סטטוס הרשמת שותף</span>
            <div className="ncd2-field-value" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" disabled={running || gi.partnerStatus === "pending"} onClick={() => act("setPartnerStatus", { status: "pending" }, "סומן ממתין לאישור")}>סמן ממתין</button>
              <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" disabled={running || !gi.partnerStatus} onClick={() => act("setPartnerStatus", { status: null }, "סטטוס נוקה")}>נקה</button>
            </div>
          </div>

          <div className="ncd2-field">
            <span className="ncd2-field-label">מזהי Grow (הזנה ידנית / קליטה משותף)</span>
            {attach.open ? (
              <div className="ncd2-field-value" style={{ display: "grid", gap: 6 }}>
                <input className="niv-push-input" dir="ltr" placeholder="userId" value={attach.userId} onChange={(e) => setAttach({ ...attach, userId: e.target.value.trim() })} />
                <input className="niv-push-input" dir="ltr" placeholder="pageCode דרישת תשלום (ריק = של הפלטפורמה מה-env)" value={attach.pageCode} onChange={(e) => setAttach({ ...attach, pageCode: e.target.value.trim() })} />
                <input className="niv-push-input" dir="ltr" placeholder="account pageCode למיתוג (אופציונלי)" value={attach.accountPageCode} onChange={(e) => setAttach({ ...attach, accountPageCode: e.target.value.trim() })} />
                <select className="niv-push-input" value={attach.environment} onChange={(e) => setAttach({ ...attach, environment: e.target.value })}>
                  <option value="sandbox">sandbox</option>
                  <option value="production">production</option>
                </select>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" className="ncd2-btn ncd2-btn-primary ncd2-btn-sm" disabled={running || !ID_RE.test(attach.userId) || (!!attach.pageCode && !ID_RE.test(attach.pageCode)) || (!!attach.accountPageCode && !ID_RE.test(attach.accountPageCode))} onClick={async () => { if (await act("attachIdentifiers", { userId: attach.userId, pageCode: attach.pageCode || undefined, accountPageCode: attach.accountPageCode || undefined, environment: attach.environment, source: "partner" }, "המזהים נשמרו והקליניקה מחוברת")) setAttach({ open: false, userId: "", pageCode: "", accountPageCode: "", environment: "sandbox" }); }}>שמירה</button>
                  <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" onClick={() => setAttach({ ...attach, open: false })}>ביטול</button>
                </div>
              </div>
            ) : (
              <button type="button" className="ncd2-btn ncd2-btn-secondary ncd2-btn-sm" onClick={() => setAttach({ ...attach, open: true })}><Plugs size={13} /> {connected ? "החלפת מזהים" : "חיבור ידני"}</button>
            )}
          </div>

          {connected && (
            <>
              <div className="ncd2-field">
                <span className="ncd2-field-label">עמלת פלטפורמה (₪ לעסקה, לפני מע"מ)</span>
                <div className="ncd2-field-value" style={{ display: "flex", gap: 6 }}>
                  <input className="niv-push-input" type="number" min={0} max={50} step={0.5} dir="ltr" placeholder={String(state?.secure?.companyCommission ?? 0)} value={commission} onChange={(e) => setCommission(e.target.value)} />
                  <button type="button" className="ncd2-btn ncd2-btn-secondary ncd2-btn-sm" disabled={running || commission === ""} onClick={async () => { if (await act("setCommission", { companyCommission: Number(commission) }, "העמלה נשמרה (נשלחת ל-Grow רק כש-GROW_COMMISSION_ON_LINKS=on)")) setCommission(""); }}>שמירה</button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button type="button" className="ncd2-btn ncd2-btn-secondary ncd2-btn-sm" disabled={running} onClick={() => setConfirm({ action: "setEnvironment", payload: { environment: env === "production" ? "sandbox" : "production" }, title: "החלפת סביבת Grow", message: `להעביר את הקליניקה ל-${env === "production" ? "sandbox" : "production"}? המזהים חייבים להתאים לסביבה החדשה, אחרת כל יצירת קישור תיכשל.`, type: "warning", ok: "הסביבה הוחלפה" })}>
                  <LinkSimple size={13} /> סביבה: {env || "—"} → {env === "production" ? "sandbox" : "production"}
                </button>
                <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" disabled={running} onClick={() => setConfirm({ action: "forceDisconnect", payload: {}, title: "ניתוק Grow בכפייה", message: "ינתק את הקליניקה מ-Grow ויבטל קישורים פעילים. התשלומים שכבר נרשמו נשארים.", type: "danger", ok: "Grow נותק" })}>
                  <Plugs size={13} /> ניתוק
                </button>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={async () => { const cfm = confirm; setConfirm(null); await act(cfm.action, cfm.payload, cfm.ok); }}
        title={confirm?.title}
        message={confirm?.message}
        type={confirm?.type || "warning"}
        confirmText="אישור"
      />
    </section>
  );
}
